import React, { useState } from 'react'
import { Card, CardContent } from '../ui/card'
import {
  AlertTriangle,
  Ship,
  Zap,
  CheckCircle,
  Star,
  Package,
  Clock,
  ArrowRight,
  Loader,
  RefreshCw,
  Pause,
  Download,
  Warehouse,
  UserCheck,
  UserPlus,
  PackageCheck,
  XCircle,
  CircleOff,
  ChevronDown,
} from 'lucide-react'

interface StatisticsCardsProps {
  total: number
  onHold: number
  vessel: number
  released: number
  unassigned: number
  assigned: number
  picking: number
  delivering: number
  onStopYard: number
  loading: number
  empty: number
  loaded: number
  returning: number
  completed: number
  cancelled: number
  pause: number
  yardStored: number
  pccHold: number
  uncompleted: number
  marked: number
  setSelectedStatus: (status: string) => void
  setShowMarkedOnly: (show: boolean) => void
  showMarkedOnly: boolean
  selectedStatus: string
  setCompletedTimeFilter: (filter: string) => void
}

interface StatusItem {
  key: string
  label: string
  value: number
  color: string
  icon: React.ElementType
  onClick: () => void
}

interface StatusGroup {
  label: string
  items: StatusItem[]
  defaultOpen: boolean
}

export const StatisticsCards: React.FC<StatisticsCardsProps> = ({
  total,
  onHold,
  vessel,
  released,
  unassigned,
  assigned,
  picking,
  delivering,
  onStopYard,
  loading,
  empty,
  loaded,
  returning,
  completed,
  cancelled,
  pause,
  yardStored,
  pccHold,
  uncompleted,
  marked,
  setSelectedStatus,
  setShowMarkedOnly,
  showMarkedOnly,
  selectedStatus,
  setCompletedTimeFilter,
}) => {
  const groups: StatusGroup[] = [
    {
      label: 'Waiting',
      defaultOpen: true,
      items: [
        { key: 'on-vessel', label: 'On Vessel', value: vessel, color: 'text-purple-600', icon: Ship, onClick: () => setSelectedStatus('on-vessel') },
        { key: 'released', label: 'Released', value: released, color: 'text-green-600', icon: Zap, onClick: () => setSelectedStatus('released') },
        { key: 'on-hold', label: 'On Hold', value: onHold, color: 'text-orange-600', icon: AlertTriangle, onClick: () => setSelectedStatus('on-hold') },
        { key: 'yard-stored', label: 'Yard Stored', value: yardStored, color: 'text-violet-600', icon: Warehouse, onClick: () => setSelectedStatus('yard-stored') },
        { key: 'pcc-hold', label: 'CBP Hold', value: pccHold, color: 'text-amber-600', icon: AlertTriangle, onClick: () => setSelectedStatus('pcc-hold') },
      ],
    },
    {
      label: 'In Progress',
      defaultOpen: true,
      items: [
        { key: 'unassigned', label: 'Unassigned', value: unassigned, color: 'text-amber-600', icon: UserPlus, onClick: () => setSelectedStatus('unassigned') },
        { key: 'assigned', label: 'Assigned', value: assigned, color: 'text-sky-600', icon: UserCheck, onClick: () => setSelectedStatus('assigned') },
        { key: 'picking', label: 'Picking', value: picking, color: 'text-blue-600', icon: Download, onClick: () => setSelectedStatus('picking') },
        { key: 'delivering', label: 'Delivering', value: delivering, color: 'text-indigo-600', icon: ArrowRight, onClick: () => setSelectedStatus('delivering') },
        { key: 'on-stop', label: 'On Stop', value: onStopYard, color: 'text-yellow-600', icon: Clock, onClick: () => setSelectedStatus('on-stop') },
        { key: 'loading', label: 'Loading', value: loading, color: 'text-cyan-600', icon: Loader, onClick: () => setSelectedStatus('loading') },
        { key: 'empty', label: 'Empty', value: empty, color: 'text-gray-600', icon: Package, onClick: () => setSelectedStatus('empty') },
        { key: 'loaded', label: 'Loaded', value: loaded, color: 'text-lime-600', icon: PackageCheck, onClick: () => setSelectedStatus('loaded') },
        { key: 'returning', label: 'Returning', value: returning, color: 'text-teal-600', icon: RefreshCw, onClick: () => setSelectedStatus('returning') },
        { key: 'paused', label: 'Paused', value: pause, color: 'text-red-600', icon: Pause, onClick: () => setSelectedStatus('paused') },
      ],
    },
    {
      label: 'Done',
      defaultOpen: false,
      items: [
        { key: 'completed', label: 'Completed', value: completed, color: 'text-emerald-600', icon: CheckCircle, onClick: () => { setCompletedTimeFilter('7days'); setSelectedStatus('completed') } },
        { key: 'uncompleted', label: 'Uncompleted', value: uncompleted, color: 'text-orange-600', icon: CircleOff, onClick: () => { setCompletedTimeFilter('7days'); setSelectedStatus('uncompleted') } },
        { key: 'cancelled', label: 'Cancelled', value: cancelled, color: 'text-rose-600', icon: XCircle, onClick: () => { setCompletedTimeFilter('7days'); setSelectedStatus('cancelled') } },
      ],
    },
  ]

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    () => Object.fromEntries(groups.map(g => [g.label, g.defaultOpen]))
  )

  const toggleGroup = (label: string) => {
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }))
  }

  const groupTotal = (items: StatusItem[]) => items.reduce((s, i) => s + (i.value || 0), 0)

  // Activity summary
  const activeTasks = (picking || 0) + (delivering || 0) + (loading || 0) + (onStopYard || 0)
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0

  return (
    <div className="w-64 flex-shrink-0 overflow-y-auto bg-[#F0F9F4]">
      <Card className="shadow-sm border-[#C8E6D0] rounded-lg overflow-hidden bg-[#F0F9F4]">
        {/* Total overview — highlighted header */}
        <div className="bg-[#F0F9F4] border-b border-[#C8E6D0] px-3 py-3">
          <button
            onClick={() => setSelectedStatus('all')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
              selectedStatus === 'all' && !showMarkedOnly
                ? 'bg-white shadow-sm border border-gray-200'
                : 'hover:bg-white/80'
            }`}
          >
            <span className="text-sm font-semibold text-slate-700">All Tasks</span>
            <span className="text-2xl font-bold text-slate-900">{(total || 0) - (completed || 0) - (uncompleted || 0) - (cancelled || 0)}</span>
          </button>
        </div>

        <CardContent className="p-2 bg-[#F0F9F4]">
          {/* Status groups */}
          <div className="space-y-0.5">
            {groups.map((group) => {
              const isOpen = openGroups[group.label]
              const count = groupTotal(group.items)
              return (
                <div key={group.label}>
                  {/* Group header */}
                  <button
                    onClick={() => toggleGroup(group.label)}
                    className="w-full flex items-center justify-between py-2 px-2.5 rounded-md hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-1.5">
                      <ChevronDown
                        className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                          isOpen ? '' : '-rotate-90'
                        }`}
                      />
                      <span className="text-[13px] font-bold text-slate-500 uppercase tracking-wide">
                        {group.label}
                      </span>
                    </div>
                    <span className={`text-[13px] font-bold ${count > 0 ? 'text-slate-700' : 'text-slate-300'}`}>
                      {count}
                    </span>
                  </button>

                  {/* Group items */}
                  <div
                    className={`overflow-hidden transition-all duration-200 ease-in-out ${
                      isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pl-2 space-y-[2px]">
                      {group.items.map((item) => {
                        const Icon = item.icon
                        const isSelected = selectedStatus === item.key && !showMarkedOnly
                        const isZero = item.value === 0
                        return (
                          <button
                            key={item.key}
                            onClick={item.onClick}
                            className={`w-full flex items-center gap-2.5 py-[7px] px-2.5 rounded-md transition-all text-left ${
                              isSelected
                                ? 'bg-[#E0F7FA] shadow-sm border border-[#B2EBF2]'
                                : isZero
                                  ? 'hover:bg-slate-50'
                                  : 'bg-slate-50/70 hover:bg-slate-100 border border-transparent hover:border-slate-200'
                            }`}
                          >
                            <Icon className={`w-4 h-4 flex-shrink-0 ${isZero ? 'text-slate-300' : item.color}`} />
                            <span className={`text-[13px] flex-1 ${isZero ? 'text-slate-300' : 'text-slate-700 font-medium'}`}>
                              {item.label}
                            </span>
                            <span className={`text-[13px] tabular-nums ${
                              isZero ? 'text-slate-300' : `font-bold ${item.color}`
                            }`}>
                              {item.value}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="border-t border-gray-100 my-2" />

          {/* Favorites */}
          <button
            onClick={() => setShowMarkedOnly(!showMarkedOnly)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all text-left ${
              showMarkedOnly
                ? 'bg-orange-50 shadow-sm border border-orange-200'
                : 'bg-slate-50/70 hover:bg-slate-100 border border-transparent hover:border-slate-200'
            }`}
          >
            <Star className={`w-4.5 h-4.5 ${showMarkedOnly ? 'text-orange-500 fill-current' : 'text-orange-400'}`} />
            <span className="text-[13px] font-medium text-slate-700 flex-1">
              {showMarkedOnly ? 'Showing Favorites' : 'Favorites'}
            </span>
            <span className={`text-sm font-bold ${marked > 0 ? 'text-orange-600' : 'text-slate-300'}`}>
              {marked}
            </span>
          </button>

          <div className="border-t border-gray-100 my-2" />

          {/* Activity Summary — ring chart style */}
          <div className="px-1 pb-1">
            <h3 className="text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-3">Activity</h3>
            <div className="flex items-center gap-4 mb-3">
              {/* Ring progress */}
              <div className="relative w-14 h-14 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="3"
                    strokeDasharray={`${completionRate * 0.942} 100`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-slate-800">{completionRate}%</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-2xl font-bold text-slate-900 leading-tight">{activeTasks}</div>
                <div className="text-[13px] text-slate-500">Active Now</div>
              </div>
            </div>
            <div className="space-y-2 text-[13px]">
              <div className="flex justify-between">
                <span className="text-slate-500">Pending Actions</span>
                <span className="font-semibold text-orange-600">{onHold + pause}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Ready to Dispatch</span>
                <span className="font-semibold text-green-600">{released}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
